import { AnalysisCustomBlock } from '@/types';
import * as localState from '../state';
import { convertCuboidToAdjustedVisibilityBoxProps, prismToAnalysisCuboid } from '@/utils/geometry';
import { File, PointOfInterest, PrismVolume } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import * as cuboidState from '@/components/Analysis/modules/cuboids/state';
import { DEFAULT_VISIBILITY_BOX } from '@/constants';
import { useCallback } from 'react';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { useIsolateCuboid } from '../../cuboids/hooks/useIsolateCuboid';
import { CuboidBase } from '@/modules/cuboids/types';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAtom, useSetAtom } from 'jotai';

export const useIsolateCustomBlock = ({ resetFocus = true }: { resetFocus?: boolean }) => {
  const setCurrentVisibilityBox = useSetAtom(state.currentVisibilityBox);
  const setIsIsolatedWorkscope = useSetAtom(cuboidState.isIsolatedWorkscope);
  const setCameraTarget = useSetAtom(state.cameraTarget);
  const [selectedCustomBlock, setSelectedCustomBlock] = useAtom(localState.selectedCustomBlock);
  const { showSnackBar } = useSnackBarMessage({});
  const { isolateCuboid } = useIsolateCuboid();
  const { checkByType } = useIsPoiWorkScope();

  const isolateWorkScope = (poi: PointOfInterest) => {
    const isWorkScope = checkByType(poi.type);
    if (!isWorkScope) return;

    const cuboid = poi?.cuboids?.[0];
    if (!cuboid) {
      showSnackBar('No cuboid data found', 'warning');
      return;
    }

    isolateCuboid(cuboid as CuboidBase, {
      focus: resetFocus,
    });
  };

  const isolateCustomBlock = useCallback(
    (block: AnalysisCustomBlock, prismData: PrismVolume, files?: File[]) => {
      if (selectedCustomBlock?.paintRegionId === block.paintRegionId) {
        return;
      }

      setSelectedCustomBlock({ ...block, files });
      const blockCuboid = prismToAnalysisCuboid({
        prismData,
        id: block.paintRegionId,
        name: block.name,
      });

      const visibilityBox = convertCuboidToAdjustedVisibilityBoxProps(blockCuboid);

      const boxCenter = visibilityBox.position;
      setCameraTarget((previous) => ({
        ...previous,
        lookAt: boxCenter,
        position: [boxCenter[0] + 10, boxCenter[1], boxCenter[2] + 50],
        zoom: 10,
      }));
      setCurrentVisibilityBox(visibilityBox);
    },
    [setCurrentVisibilityBox, setCameraTarget, setSelectedCustomBlock, selectedCustomBlock]
  );

  const deSelectCustomBlock = useCallback(() => {
    setCurrentVisibilityBox(DEFAULT_VISIBILITY_BOX);
    setSelectedCustomBlock(undefined);
    setCameraTarget(undefined);
    setIsIsolatedWorkscope(false);
  }, [setCameraTarget, setCurrentVisibilityBox, setIsIsolatedWorkscope, setSelectedCustomBlock]);

  return {
    isolateCustomBlock,
    deSelectCustomBlock,
    isolateWorkScope,
  };
};
